import { useCallback, useState } from "react";
import Designer from "./Designer";
import { normalizeRailedEdges, rectangleVertices } from "./lib/geometry";
import type { Design } from "./lib/types";

export type { Design };

const DEFAULT_VERTICES = rectangleVertices(16, 12);
const DEFAULT_DESIGN: Design = {
  vertices: DEFAULT_VERTICES,
  railHeightInches: 36,
  color: "black",
  stairs: {
    enabled: false,
    edgeIndex: 0,
    lengthFt: 8,
    withRailing: true,
  },
  railedEdges: normalizeRailedEdges(undefined, DEFAULT_VERTICES.length),
};

function cloneDesign(design: Design): Design {
  return {
    vertices: design.vertices.map((p) => ({ ...p })),
    railHeightInches: design.railHeightInches,
    color: design.color,
    stairs: { ...design.stairs },
    railedEdges: design.railedEdges.slice(),
  };
}

function normalizeDesign(design: Design): Design {
  const next = cloneDesign(design);
  if (next.railedEdges.length !== next.vertices.length) {
    next.railedEdges = normalizeRailedEdges(next.railedEdges, next.vertices.length);
  }
  return next;
}

function applyDesignPatch(design: Design, patch: Partial<Design>): Design {
  return normalizeDesign({ ...design, ...patch });
}

export default function App() {
  const [design, setDesign] = useState(() => normalizeDesign(DEFAULT_DESIGN));

  const update = useCallback(
    (patch: Partial<Design>) => {
      setDesign((current) => applyDesignPatch(current, patch));
    },
    [],
  );

  const reset = useCallback(() => {
    setDesign(normalizeDesign(DEFAULT_DESIGN));
  }, []);

  return (
    <Designer
      design={design}
      onChange={update}
      onReset={reset}
    />
  );
}
